import React from 'react';
import { FileText, Download, CheckCircle2, AlertTriangle, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { matters as mattersApi, lodgement } from '@/lib/api';
import { ELodgePanel } from './ELodgePanel';

export default function LodgementPage() {
  const [ms, setMs] = React.useState([]);
  const [matterId, setMatterId] = React.useState('');
  const [pre, setPre] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    mattersApi.list().then((rows) => {
      setMs(rows);
      if (rows.length > 0) setMatterId(rows[0].id);
    });
  }, []);

  React.useEffect(() => {
    if (!matterId) return;
    setLoading(true); setErr(''); setPre(null);
    lodgement.prefill(matterId)
      .then(setPre)
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to prefill'))
      .finally(() => setLoading(false));
  }, [matterId]);

  const download = async () => {
    setDownloading(true);
    try { await lodgement.downloadBundle(matterId, pre ? `ImmiPro_${pre.matter_ref}_Bundle.pdf` : null); }
    catch (e) { setErr('Bundle download failed'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Lodgement Workspace</h1>
          <div className="text-sm text-slate-500">Assisted-fill from live matter data · DHA statutory form layout · one-click bundle</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={matterId} onValueChange={setMatterId}>
            <SelectTrigger className="h-10 w-72"><SelectValue placeholder="Pick a matter…" /></SelectTrigger>
            <SelectContent>
              {ms.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} — {m.applicant} ({m.subclass})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={download} disabled={downloading || !pre} variant="outline" className="h-10 gap-1.5 border-slate-200">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download bundle (PDF)
          </Button>
          <Button className="h-10 gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"><Send className="h-4 w-4" /> Ready for lodgement</Button>
        </div>
      </div>

      {loading && <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>}
      {err && <div className="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{err}</div>}

      {pre && (
        <>
          <div className="mt-6 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4 text-sm text-[#1A3A5C]">
            <span className="font-semibold">{pre.form_code}</span> · Matter <span className="font-mono">{pre.matter_ref}</span> · Applicant <span className="font-medium">{pre.applicant}</span> · Subclass {pre.subclass}{pre.stream ? ` (${pre.stream})` : ''}. Values pre-filled from live matter record.
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              {pre.sections.map((sec) => (
                <Card key={sec.label} className="border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-[#1A3A5C]">{sec.label}</div>
                      <div className="text-xs text-slate-400">{sec.fields.length} fields</div>
                    </div>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      {sec.fields.map((f) => (
                        <div key={f.attribute + f.label}>
                          <Label className="text-xs">{f.label}</Label>
                          <Input defaultValue={f.value} className="mt-1 h-9" />
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">Source: {f.attribute}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="space-y-4">
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Pre-lodgement validation</div>
                  <div className="mt-3 space-y-2">
                    {pre.validations.map((v, i) => (
                      <div key={i} className={`flex items-start gap-2 rounded-md p-2 text-xs ${v.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                        {v.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />}
                        <span>{v.msg}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <ELodgePanel matter={ms.find((x) => x.id === matterId)} />
              <div className="text-[11px] text-slate-400">Bundle generated at {new Date(pre.generated_at).toLocaleString()}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
